// Third-party
import uniq from 'lodash/uniq';

// Constants and types
import Dictionary from '@/types/dictionary';
import Fund from '@/types/fund';
import AppRegion from '@/types/app-region';
import Benchmark from '@/types/benchmark';
import ComparisonItem from '@/types/comparison-item';
import BasicBenchmark from '@/types/basic-benchmark';
import Risk from '@/types/risk';
import Comparison from '@/types/comparison';
import LocalizedString from '@/types/localized-string';
import Shelf from '@/types/shelf';

// Utils
import { addCommonInceptionToPerformance, benchmarkToBasicBenchmark, getAssetAllocations } from './shared';
import { COMPARISON_ITEM_TYPES, Interval } from '@/utils/constants';
import { HOLDING_TYPES } from '@/utils/portfolios';
import disclaimers from '@/utils/disclaimers';
import { fundToInvestmentItem } from '@/utils/funds';
import { riskRatingLiteralToAllocations } from '@/utils/risk';
import { convertShelvesToShelfStatuses } from '@/utils/shelf-management';
import { getCouponDates } from '../../../modules/proposal/utils/bonds-utils';
import { deepClone } from '@/utils/objects';
import { calculateRiskMetrics } from '@/utils/calculation-utils';
import { setCommonInceptionRiskMetrics } from '@/utils/common-inception';
import { processAllocationsForRegion } from '@/utils/allocations';

const fundCommonInceptionRiskMetrics = (fund: Fund, benchmarks?: Benchmark[], comparison?: Comparison) => {
  const clone = deepClone(fund);
  const { performance, benchmarkLipperId, riskFreeLipperId } = clone;
  const marketBenchmark = benchmarks && benchmarks.find(b => b.lipperId === benchmarkLipperId);
  const riskFreeBenchmark = benchmarks && benchmarks.find(b => b.lipperId === riskFreeLipperId);

  // TODO CONN-4280 -- Confirm logic for risk metrics, CURRENCY.USD shouldn't map rollingUSD
  // const useBaseCurrency = currency === CURRENCY.USD;
  // const rollingBase = useBaseCurrency ? 'rollingUSD' || 'rolling' : 'rolling';

  const marketRolling = (marketBenchmark && marketBenchmark.performance.rolling[Interval.Monthly]) || [];
  const riskFreeRolling = (riskFreeBenchmark && riskFreeBenchmark.performance.rolling[Interval.Monthly]) || [];

  // performance calculation
  const pRolling = deepClone(performance.rolling[Interval.Monthly]);
  const pRiskMetrics = calculateRiskMetrics(pRolling, marketRolling, riskFreeRolling, false, comparison?.commonInception);
  clone.performance = {
    ...clone.performance,
    ...setCommonInceptionRiskMetrics(clone.performance, pRiskMetrics),
  };

  // performanceBase calculation
  if (clone.performanceBase) {
    const pbRolling = deepClone(clone.performanceBase.rolling[Interval.Monthly]);
    const pbRiskMetrics = calculateRiskMetrics(pbRolling, marketRolling, riskFreeRolling, false, comparison?.commonInception);
    clone.performanceBase = {
      ...clone.performanceBase,
      ...setCommonInceptionRiskMetrics(clone.performanceBase, pbRiskMetrics),
    };
  }

  return clone;
};

/**
 * Generates a comparison item from a single fund.
 *
 * @param {Fund} fund Fund model object.
 * @return {ComparisonItem} Comparison item based on the fund.
 */
const fundToComparisonItem = (
  fund: Fund,
  appRegion: AppRegion,
  benchmarks?: Benchmark[],
  options?: {
    itemFlags?: string[];
    itemSymbol?: string;
    comparison?: Comparison;
  },
): ComparisonItem => {
  const riskFreeBenchmarks = benchmarks
    ? benchmarks.reduce((acc, curr) => {
        if (curr.lipperId === fund.riskFreeLipperId) {
          acc[fund._id] = benchmarkToBasicBenchmark(curr);
        }
        return acc;
      }, {} as Dictionary<BasicBenchmark>)
    : undefined;

  const marketBenchmarks = benchmarks
    ? benchmarks.reduce((acc, curr) => {
        if (curr.lipperId === fund.benchmarkLipperId) {
          acc[fund._id] = benchmarkToBasicBenchmark(curr);
        }
        return acc;
      }, {} as Dictionary<BasicBenchmark>)
    : undefined;

  fund = {
    ...fund,
    ...fundCommonInceptionRiskMetrics(fund, benchmarks, options?.comparison),
  };

  const risk: Risk = {
    standardDeviation: fund.performance.standardDeviation,
    maximumDrawdown: fund.performance.maximumDrawdown,
    correlationCoefficient: fund.performance.correlationCoefficient,
    rSquared: fund.performance.rSquared,
    downside: fund.performance.downside,
    infoRatio: fund.performance.infoRatio,
    trackingError: fund.performance.trackingError,
    upside: fund.performance.upside,
    alpha: fund.performance.alpha,
    beta: fund.performance.beta,
    sharpeRatio: fund.performance.sharpeRatio,
    sortino: fund.performance.sortino,
  };

  const performance = addCommonInceptionToPerformance(fund.performance, options?.comparison?.commonInception?.startTimestamp);

  const performanceBase = addCommonInceptionToPerformance(fund.performanceBase || fund.performance, options?.comparison?.commonInception?.startTimestamp);

  const investmentItem = fundToInvestmentItem(fund);
  const flags = options?.itemFlags || fund.flags;

  let detailedAsset = fund.fundPortfolio?.allocations?.asset;
  if (detailedAsset?.length) {
    detailedAsset = processAllocationsForRegion(detailedAsset, appRegion);
  }

  return {
    _id: fund._id,
    type: COMPARISON_ITEM_TYPES.FUND,
    name: fund?.names?.full as LocalizedString,
    currency: fund.currency,
    holdings: [
      {
        id: fund._id,
        type: HOLDING_TYPES.FUND,
        // If a comparison item already defines a symbol then we make use of it instead of defaulting to the first symbol of the fund.symbols array
        symbol: options?.itemSymbol || (fund.symbols ? fund.symbols[0] : undefined),
        symbols: fund.symbols,
        name: fund?.names?.full,
        weight: 100,
        rolling: performance.rolling,
        baseRolling: performanceBase.rolling,
        nav: fund.nav,
        distribution: fund.distribution,
        filteredShelves: fund.filteredShelves,
      },
    ],
    performance,
    performanceBase,
    originalRisk: { ...risk },
    classification: fund.classification,
    objective: fund.objective,
    mer: fund.mer,
    minInvestments: fund.minInvestments && {
      currency: fund.minInvestments && fund.minInvestments.currency,
      value: fund.minInvestments && fund.minInvestments.initial,
    },
    regularInvestments: fund.minInvestments && {
      currency: fund.minInvestments && fund.minInvestments.currency,
      value: fund.minInvestments && fund.minInvestments.regular,
    },
    rrspEligible: fund.rrspEligible,
    allocations: fund.fundPortfolio &&
      fund.fundPortfolio.allocations && {
        asOfDate: fund.fundPortfolio.allocations.asOfDate,
        asset: fund.fundPortfolio.allocations.asset && getAssetAllocations(detailedAsset!, appRegion),
        detailedAsset,
        sector: fund.fundPortfolio.allocations.sector,
        country: fund.fundPortfolio.allocations.country,
        creditQuality: fund.fundPortfolio.allocations.creditQuality,
        maturity: fund.fundPortfolio.allocations.maturity,
        fixedIncomeRegion: fund.fundPortfolio.allocations.fixedIncomeRegion,
        fixedIncomeSector: fund.fundPortfolio.allocations.fixedIncomeSector,
        region: fund.fundPortfolio.allocations.region,
        equityCountry: fund.fundPortfolio.allocations.equityCountry,
        fixedIncomeCountry: fund.fundPortfolio.allocations.fixedIncomeCountry,
        equityRegion: fund.fundPortfolio.allocations.equityRegion,
      },
    riskRating: riskRatingLiteralToAllocations(fund.riskRating),
    riskAssetType: !fund.riskAssetType
      ? undefined
      : {
          [fund.riskAssetType]: 100,
        },
    tna: fund.tna,
    lipperLeaders: fund.lipperLeaders,
    detailedHoldings: fund.fundPortfolio && fund.fundPortfolio.holdings,
    disclaimers: disclaimers.getComparisonFundDisclaimers(fund, benchmarks),
    dates: fund.dates,
    trustedSource: fund.trustedSource,
    benchmarks: {
      market: marketBenchmarks && Object.keys(marketBenchmarks).length ? marketBenchmarks : undefined,
      riskFree: riskFreeBenchmarks && Object.keys(riskFreeBenchmarks).length ? riskFreeBenchmarks : undefined,
    },
    characteristics: fund.fundPortfolio &&
      fund.fundPortfolio.characteristics && {
        averageCoupon: fund.fundPortfolio.characteristics.averageCoupon,
        averageQuality: fund.fundPortfolio.characteristics.averageQuality,
        yieldToMaturity: fund.fundPortfolio.characteristics.yieldToMaturity,
        returnOnEquity: fund.fundPortfolio.characteristics.returnOnEquity,
        faceValue: fund.fundPortfolio.characteristics.faceValue,
        maturityDate: fund.fundPortfolio.characteristics.maturityDate,
        frequency: fund.fundPortfolio.characteristics.frequency,
        frequencyUnit: fund.fundPortfolio.characteristics.frequencyUnit,
        couponDates: getCouponDates(fund.fundPortfolio.characteristics.frequency, fund.fundPortfolio.characteristics.frequencyUnit),
        effectiveMaturity: fund.fundPortfolio.characteristics.effectiveMaturity,
      },
    investmentInfo: {
      modifiedDuration: investmentItem.modifiedDuration,
      effectiveDuration: investmentItem.effectiveDuration,
      peRatio: investmentItem.peRatio,
      pbRatio: investmentItem.pbRatio,
      distributionYield: investmentItem.distributionYield,
      style: investmentItem.style,
      capitalization: investmentItem.capitalization,
      durationClass: investmentItem.durationClass,
      creditRatingClass: investmentItem.creditRatingClass,
    },
    manufacturer: fund.manufacturer,
    universe: fund.universe,
    esg: fund.esg,
    flags: uniq([...(flags || []), ...convertShelvesToShelfStatuses(fund.filteredShelves as Shelf[], fund.complianceComparisons)]),
    customData: fund.customData,
  };
};

export default fundToComparisonItem;
